import React from 'react';
import * as Yup from 'yup';
import { FormikErrors, FormikTouched, FormikProps } from 'formik';
import {
  Step as StepProps,
  StepConsumerContext
} from '@service-victoria/component-library/components/Wizard/props';
import { memoize } from 'lodash-es';
import * as Interfaces from 'interfaces';
import { constants, validations } from 'utils';
import { Step as Identity } from '../Identity';
import { Step as Review } from '../Review';
import { addressSchema } from 'Components/Address/helpers';
import { GenericValidationErrors, PageConfig, ReturningHomeEndDate } from 'utils/constants';
import {
  isExemptionApplication,
  isBorderResident,
  isCrossBorderResident,
  isReturnExemption,
  isFullVaxERZVictorian,
  isInternationalArrival
} from './travel-reasons';
import {
  showExtremelyRiskZone,
  showOrangeZoneWarning,
  showRedZoneWarning,
  showGreenZoneWarning,
  showReturningHomeInvalidWarning,
  showTransitInvalidWarningAll
} from './selectors';
import { doesNotContainEmoji } from 'utils/validations';
import { countryCodes } from 'interfaces/common';
import dayjs from 'dayjs';
import { scrollToError } from '@service-victoria/component-library/utils/helpers';
import { Values } from './initialValues';
export { initialValues } from './initialValues';

const initialErrors: FormikErrors<Values> = {};
const initialTouched: FormikTouched<Values> = {};

const licenceErrors = {
  ACT: 'Please enter a valid Australian Capital Territory driver licence number. It can be up to 10 digits and can only contain numbers.',
  NSW: "That doesn't look right. A New South Wales driver licence is 6 to 8 characters with no spaces.",
  NT: "That doesn't look right. A Northern Territory driver licence is between 1 and 10 characters.",
  QLD: "That doesn't look right. A Queensland driver licence is 8 or 9 characters.",
  SA: "That doesn't look right. A South Australian driver licence is 6 characters.",
  TAS: "That doesn't look right. A Tasmanian driver licence is 6 to 8 characters.",
  VIC: "That doesn't look right. A Victorian driver licence is up to 10 character",
  WA: "That doesn't look right. A Western Australian driver licence is 7 characters"
};

const departSchema = memoize((reason: string, schema: Yup.MixedSchema) => {
  const isInternationalArrivalPermit = isInternationalArrival(reason);
  const returnExemptionDateParams = isReturnExemption(reason)
    ? [ReturningHomeEndDate, dayjs(ReturningHomeEndDate).format('D MMMM, YYYY')]
    : [];
  return Yup.object().shape<Interfaces.Common.Depart>({
    date: validations.date.dateOfEvent(
      'FullDate',
      isInternationalArrivalPermit,
      ...returnExemptionDateParams
    ),
    country: Yup.mixed().required(constants.GenericValidationErrors.required),
    quarantine: Yup.object().shape<Interfaces.Common.QuarantineDetails>({
      fromQuarantine: Yup.boolean(),
      hotel: Yup.string().when(
        'fromQuarantine',
        (fromQuarantine: boolean, schema: Yup.StringSchema) => {
          if (fromQuarantine) {
            return schema
              .required(GenericValidationErrors.required)
              .test(
                'isFromQuarantineValid',
                GenericValidationErrors.genericError,
                doesNotContainEmoji
              );
          }
          return schema.notRequired();
        }
      )
    }),
    from:
      isCrossBorderResident(reason) || isFullVaxERZVictorian(reason)
        ? schema.notRequired()
        : Yup.string().required(constants.GenericValidationErrors.required),
    fromAddressCountry: Yup.string<countryCodes>().required(
      constants.GenericValidationErrors.required
    ),
    fromAddress: isCrossBorderResident(reason) ? schema.notRequired() : addressSchema,
    homeAddressCountry: Yup.string().required(constants.GenericValidationErrors.required),
    homeAddress: Yup.mixed().when('from', (from, schema: Yup.MixedSchema) => {
      return from === 'home'
        ? schema.notRequired()
        : Yup.mixed().when('homeAddressCountry', {
            is: 'AUS',
            then: addressSchema,
            otherwise: Yup.mixed().notRequired()
          });
    }),
    targetPlace: Yup.mixed().when(
      'homeAddressIsDestination',
      (homeAddressIsDestination, schema: Yup.MixedSchema) => {
        return homeAddressIsDestination || isCrossBorderResident(reason)
          ? schema.notRequired()
          : Yup.string()
              .required(constants.AddressValidationErrors.place)
              .test(
                'isTargetPlaceValid',
                constants.AddressValidationErrors.place,
                doesNotContainEmoji
              );
      }
    ),
    target: Yup.mixed().when(
      'homeAddressIsDestination',
      (homeAddressIsDestination, schema: Yup.MixedSchema) => {
        return homeAddressIsDestination || isCrossBorderResident(reason)
          ? schema.notRequired()
          : addressSchema;
      }
    ),
    returningVicInvalidDestination: Yup.boolean(),
    returningVicInvalidHome: Yup.boolean(),
    homeAddressIsDestination: Yup.boolean()
  });
});

const wizardSchema = Yup.object().shape<Interfaces.Common.Assess>({
  vaccinationStatus: Yup.string().required(constants.GenericValidationErrors.required),
  Depart: Yup.mixed().when('reasons', departSchema),
  reasons: Yup.string().test(
    'atLeastOneReasonSelected',
    GenericValidationErrors.required,
    function validate(reasons?: string) {
      return !!reasons;
    }
  ),
  exemptionReason: Yup.mixed().when(['reasons'], (reason: string, schema: Yup.MixedSchema) => {
    if (isExemptionApplication(reason)) {
      return schema.test(
        'isReasonSelected',
        GenericValidationErrors.required,
        function validate(value?: Interfaces.Common.ExemptionReason[]) {
          return !!(value && value.length > 0 && value.find(r => r && r.selected));
        }
      );
    }
    return schema.notRequired();
  }),
  negativeTest: Yup.string().when(['reasons'], (reason: string, schema: Yup.StringSchema) => {
    if (isExemptionApplication(reason)) {
      return schema.required(GenericValidationErrors.required);
    }
    return schema.notRequired();
  }),
  circumstances: Yup.string().when(['reasons'], (reason: string, schema: Yup.StringSchema) => {
    if (isExemptionApplication(reason)) {
      return schema
        .required(GenericValidationErrors.required)
        .test('isCircumstancesValid', GenericValidationErrors.genericError, doesNotContainEmoji);
    }
    return schema.notRequired();
  }),
  returningReason: Yup.string().when(['reasons'], (reason: string, schema: Yup.StringSchema) => {
    if (isReturnExemption(reason)) {
      return schema.required(GenericValidationErrors.required);
    }
    return schema.notRequired();
  }),
  hasDriverLicence: Yup.string().when(['reasons'], (reason: string, schema: Yup.StringSchema) => {
    if (isExemptionApplication(reason) || isReturnExemption(reason)) {
      return schema.required(GenericValidationErrors.required);
    }
    return schema.notRequired();
  }),
  SupportingDocuments: Yup.object()
    .when('hasDriverLicence', {
      is: 'yes',
      then: Yup.object().shape<Interfaces.Common.SupportingDocuments>({
        driversLicence: Yup.object().shape<Interfaces.Common.DriversLicence>({
          registrationState: Yup.string().required(constants.GenericValidationErrors.required),
          licenceNo: Yup.string()
            .required(constants.GenericValidationErrors.required)
            .test('isLicenseNoValid', GenericValidationErrors.genericError, doesNotContainEmoji)
            .when('registrationState', {
              is: val => val === 'ACT',
              then: Yup.string()
                .trim()
                .min(1, licenceErrors.ACT)
                .max(10, licenceErrors.ACT)
                .required(constants.GenericValidationErrors.required)
            })
            .when('registrationState', {
              is: val => val === 'NT',
              then: Yup.string()
                .trim()
                .min(1, licenceErrors.NT)
                .max(10, licenceErrors.NT)
                .required(constants.GenericValidationErrors.required)
            })
            .when('registrationState', {
              is: val => val === 'QLD',
              then: Yup.string()
                .trim()
                .min(8, licenceErrors.QLD)
                .max(9, licenceErrors.QLD)
                .required(constants.GenericValidationErrors.required)
            })
            .when('registrationState', {
              is: val => val === 'NSW',
              then: Yup.string()
                .trim()
                .min(6, licenceErrors.NSW)
                .max(8, licenceErrors.NSW)
                .required(constants.GenericValidationErrors.required)
            })
            .when('registrationState', {
              is: val => val === 'SA',
              then: Yup.string()
                .trim()
                .length(6, licenceErrors.SA)
                .required(constants.GenericValidationErrors.required)
            })
            .when('registrationState', {
              is: val => val === 'TAS',
              then: Yup.string()
                .trim()
                .min(6, licenceErrors.TAS)
                .max(8, licenceErrors.TAS)
                .required(constants.GenericValidationErrors.required)
            })
            .when('registrationState', {
              is: val => val === 'VIC',
              then: Yup.string()
                .trim()
                .min(1, licenceErrors.VIC)
                .max(10, licenceErrors.VIC)
                .required(constants.GenericValidationErrors.required)
            })
            .when('registrationState', {
              is: val => val === 'WA',
              then: Yup.string()
                .trim()
                .length(7, licenceErrors.WA)
                .required(constants.GenericValidationErrors.required)
            }),
          givenNames: Yup.string()
            .required(constants.GenericValidationErrors.required)
            .test(
              'isDriverLicenseGivenNameValid',
              GenericValidationErrors.genericError,
              doesNotContainEmoji
            ),
          familyName: Yup.string()
            .required(constants.GenericValidationErrors.required)
            .test(
              'isDriverLicenseFamilyNameValid',
              GenericValidationErrors.genericError,
              doesNotContainEmoji
            ),
          dateOfBirth: validations.date.dateOfBirth('FullDate'),
          consent: Yup.mixed().test(
            'isTicked',
            'Please tick this box to proceed.',
            function validate(value: boolean) {
              return value;
            }
          )
        })
      }),
      otherwise: Yup.object().notRequired()
    })
    .when(['reasons'], (reason: string, schema: Yup.ObjectSchema) => {
      if (isExemptionApplication(reason) || isReturnExemption(reason)) {
        return schema;
      }
      return schema.notRequired();
    }),
  travelZone: Yup.string().required('You must select a reason for travel.')
});

const validationSchema = Yup.object().shape<Values>({
  assess: wizardSchema
}) as Yup.Schema<Values>;

const Component = React.lazy(() => import('./Assess'));

const Step: StepProps<Values> = {
  id: 'assess',
  title: constants.Page.title,
  stepName: 'Travel details',
  validation: {
    schema: validationSchema,
    fieldNames: [
      'assess.Depart.date',
      'assess.Depart.from',
      'assess.Depart.targetPlace',
      'assess.Depart.target',
      'assess.Depart.target.fullDetails',
      'assess.Depart.target.fullDetails.fullAddress',
      'assess.Depart.target.fullDetails.postCode',
      'assess.Depart.target.fullDetails.state',
      'assess.Depart.target.fullDetails.suburb',
      'assess.Depart.target.summaryData',
      'assess.Depart.target.isOverseasAddress',
      'assess.Depart.target.overseas',
      'assess.Depart.target.overseas.country',
      'assess.Depart.target.overseas.info',
      'assess.Depart.target.overseas.info.basic',
      'assess.Depart.fromAddress',
      'assess.Depart.fromAddress.fullDetails',
      'assess.Depart.fromAddress.fullDetails.fullAddress',
      'assess.Depart.fromAddress.fullDetails.postCode',
      'assess.Depart.fromAddress.fullDetails.state',
      'assess.Depart.fromAddress.fullDetails.suburb',
      'assess.Depart.fromAddress.summaryData',
      'assess.Depart.fromAddress.isOverseasAddress',
      'assess.Depart.fromAddress.overseas',
      'assess.Depart.fromAddress.overseas.country',
      'assess.Depart.fromAddress.overseas.info',
      'assess.Depart.fromAddress.overseas.info.basic',
      'assess.Depart.homeAddress',
      'assess.Depart.homeAddress.fullDetails',
      'assess.Depart.homeAddress.fullDetails.fullAddress',
      'assess.Depart.homeAddress.fullDetails.postCode',
      'assess.Depart.homeAddress.fullDetails.state',
      'assess.Depart.homeAddress.fullDetails.suburb',
      'assess.Depart.homeAddress.summaryData',
      'assess.Depart.homeAddress.isOverseasAddress',
      'assess.Depart.homeAddress.overseas',
      'assess.Depart.homeAddress.overseas.country',
      'assess.Depart.homeAddress.overseas.info',
      'assess.Depart.homeAddress.overseas.info.basic',
      'assess.Depart.quarantine.hotel',
      'assess.reasons',
      'assess.exemptionReason',
      'assess.negativeTest',
      'assess.circumstances',
      'assess.returningReason',
      'assess.hasDriverLicence',
      'assess.SupportingDocuments.driversLicence.registrationState',
      'assess.SupportingDocuments.driversLicence.licenceNo',
      'assess.SupportingDocuments.driversLicence.givenNames',
      'assess.SupportingDocuments.driversLicence.familyName',
      'assess.SupportingDocuments.driversLicence.dateOfBirth',
      'assess.SupportingDocuments.driversLicence.consent',
      'assess.travelZone',
      'assess.vaccinationStatus',
      ...(PageConfig.IS_RETURN_ENABLED ? ['assess.returningBy', 'assess.returningByDate'] : [])
    ]
  },
  Component,
  navigation: {
    normal: [
      {
        content: 'Back',
        type: 'secondary',
        shouldValidate: false,
        onClick: (wizardContext, formProps) => {
          wizardContext.dispatch({
            type: 'GOTO_STEP',
            stepId: Identity.id,
            values: formProps.values
          });
        }
      },
      {
        content: 'Next',
        shouldValidate: true,
        type: 'primary',
        onClick: (context: StepConsumerContext<Values>, form: FormikProps<Values>) => {
          if (isBorderResident(form.values.assess.reasons)) {
            // They shouldn't be able to get this far... The submission is hidden
            return;
          } else if (
            form.values.assess.Depart.returningVicInvalidDestination ||
            form.values.assess.Depart.returningVicInvalidHome ||
            showExtremelyRiskZone(form.values) ||
            showRedZoneWarning(form.values) ||
            showOrangeZoneWarning(form.values) ||
            showGreenZoneWarning(form.values) ||
            showReturningHomeInvalidWarning(form.values) ||
            showTransitInvalidWarningAll(form.values)
          ) {
            scrollToError();
            // Prevent user from progressing
            return;
          } else {
            context.dispatch({
              type: 'GOTO_STEP',
              stepId: Review.id,
              values: form.values
            });
          }
        }
      }
    ]
  }
};

export { Step, initialErrors, wizardSchema, initialTouched };
